import { Box, Button, Typography, useTheme } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import {
  useGetPaymentsMutation,
  useInitializeRazorpayMutation,
  useRecordPaymentMutation,
} from "../../features/auth/apiSlice";
import { useSelector } from "react-redux";
import { selectSponserID, selectUserData } from "../../features/auth/authSlice";

const __Dev__ = document.domain === "localhost";

const Index = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [data, setData] = useState([] as any);
  const [getpaymentsAPI] = useGetPaymentsMutation();
  const sponserId = useSelector(selectSponserID);
  const userData = useSelector(selectUserData);
  const [initializeRazorpayAPI] = useInitializeRazorpayMutation();
  const [recordPaymentAPI] = useRecordPaymentMutation();

  const getPayments = async () => {
    const response: any = await getpaymentsAPI({ sponserId }).unwrap();
    const modifiedData = response?.payments.map((payment: any) => {
      return { ...payment, ...payment?.feedId, _id: payment._id };
    });
    setData(modifiedData);
  };

  useEffect(() => {
    getPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sponserId]);

  const loadRazorpay = (src: string) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      document.body.appendChild(script);
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        reject(true);
      };
    });
  };

  const displayRazorpay = async ({
    id,
    paymentId,
  }: {
    id: string;
    paymentId: string;
  }) => {
    const response: any = await loadRazorpay(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!response) {
      console.log("Razorpay SDK failed to load are you online");
      return;
    }

    const razorpayResponse: any = await initializeRazorpayAPI({
      sponserId,
      paymentId: id,
    }).unwrap();

    var options = {
      //TODO add production api
      key: __Dev__ ? "rzp_test_b6eM0MKyD4lhO5" : "Production API", // Enter the Key ID generated from the Dashboard
      amount: razorpayResponse.amount, // Amount is in currency subunits. Default currency is INR. Hence, 50000 refers to 50000 paise
      currency: razorpayResponse.currency,
      name: userData.name, //your business name
      description: "Feed Transaction",
      image: data?.imageUrl,
      order_id: razorpayResponse.id, //This is a sample Order ID. Pass the `id` obtained in the response of Step 1
      // callback_url: "http://localhost:3000/invoices",
      handler: async (response: any) => {
        const recordPaymentResponse: any = await recordPaymentAPI({
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
          sponserId,
          paymentId: paymentId,
        }).unwrap();
        if (recordPaymentResponse.status === 200) {
          const modifiedData = recordPaymentResponse?.payments.map(
            (payment: any) => {
              return { ...payment, ...payment?.feedId, _id: payment._id };
            }
          );
          getPayments();
          setData(modifiedData);
        }
      },
      prefill: {
        name: userData.name,
        email: userData.email,
      },
    };

    const _window = window as any;
    var paymentObject = new _window.Razorpay(options);
    paymentObject.open();
  };

  const columns = [
    { field: "_id", headerName: "ID", width: 190 },
    {
      field: "title",
      headerName: "Feed Title",
      cellClassName: "name-column--cell",
      width: 150,
    },
    {
      field: "feedId",
      headerName: "Feed Id",
      cellClassName: "name-column--cell",
      renderCell: (params: any) => {
        return <div key={params.row.feedId._id}>{params.row.feedId._id}</div>;
      },
      width: 190,
    },
    {
      field: "link",
      headerName: "Product Link",
      cellClassName: "name-column--cell",
      width: 200,
    },
    {
      field: "price",
      headerName: "Price",
      renderCell: (params: any) => {
        return (
          <Typography color={colors.greenAccent[500]}>
            {params.row.total_amount} Rs
          </Typography>
        );
      },
      width: 100,
    },
    {
      field: "action",
      headerName: "Action",
      renderCell: (params: any) => {
        return (
          <>
            {params?.row?.status !== "done" ? (
              <Button
                variant="contained"
                onClick={() =>
                  displayRazorpay({ id: params.id, paymentId: params.row._id })
                }
              >
                Pay Now
              </Button>
            ) : (
              <Typography
                variant="h5"
                style={{ color: colors.greenAccent[400] }}
              >
                Paid
              </Typography>
            )}
          </>
        );
      },
      width: 100,
    },
  ];

  return (
    <div className="ml-[16px] mt-[100px]">
      <Header title="Payments" subtitle="List of payments" />
      <Box
        m={"40px 0 0 0"}
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "noone",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          components={{ Toolbar: GridToolbar }}
          checkboxSelection
          rows={data}
          /*@ts-ignore */
          columns={columns}
          autoHeight={true}
          getRowId={(row) => row._id}
        />
      </Box>
    </div>
  );
};

export default Index;
